import type { Assertion, PropertyDefinition } from '@peruselab/api-client/assertions';

/** How a UCUM unit code is written, from the catalog; SI units the catalog may not list are covered too. */
const SI_SYMBOLS: Record<string, string> = { K: 'K', Pa: 'Pa', 'kg/m3': 'kg/m³', '1': '' };

export function unitSymbol(code: string, property?: PropertyDefinition): string {
  return property?.units.find((unit) => unit.code === code)?.symbol ?? SI_SYMBOLS[code] ?? code;
}

function withUnit(text: string, symbol: string): string {
  if (!symbol) return text;
  // "135 °C", "1.4 g/cm³": a space before the unit (SI style), none for a bare number.
  return `${text} ${symbol}`;
}

const SUPERSCRIPT: Record<string, string> = {
  '-': '⁻',
  '+': '',
  '0': '⁰',
  '1': '¹',
  '2': '²',
  '3': '³',
  '4': '⁴',
  '5': '⁵',
  '6': '⁶',
  '7': '⁷',
  '8': '⁸',
  '9': '⁹'
};

/** #929: "2.52E-5" reads as "2.52 × 10⁻⁵"; any other number is shown exactly as reported. */
export function readableNumber(text: string): string {
  const scientific = /^(-?\d+(?:\.\d+)?)[eE]([-+]?\d+)$/.exec(text.trim());
  if (!scientific) return text;
  const exponent = [...scientific[2]!].map((character) => SUPERSCRIPT[character] ?? character).join('');
  return `${scientific[1]} × 10${exponent}`;
}

/** ADR 0018 D7 (#915): the value as reported -- "135.0 ± 0.5 °C", "137–138 °C", "1.5". */
export function formatValue(assertion: Pick<Assertion, 'value'>, property?: PropertyDefinition): string {
  const { unit, uncertainty } = assertion.value;
  const value = readableNumber(assertion.value.value);
  const upper = assertion.value.upper ? readableNumber(assertion.value.upper) : null;
  const range = upper ? `${value}–${upper}` : value;
  const withUncertainty = uncertainty ? `${range} ± ${uncertainty}` : range;
  return withUnit(withUncertainty, unitSymbol(unit, property));
}

/**
 * The SI form, when it adds something -- "(408.15 K)" beside "135 °C"; nothing when the value was
 * already given in the SI unit or is a bare number.
 */
export function formatSi(
  assertion: Pick<Assertion, 'value' | 'siValue' | 'siUpper' | 'siUnit'>,
  language: string
): string | null {
  if (assertion.value.unit === assertion.siUnit || assertion.siUnit === '1') return null;
  const number = new Intl.NumberFormat(language, { maximumSignificantDigits: 6 });
  const symbol = SI_SYMBOLS[assertion.siUnit] ?? assertion.siUnit;
  const range =
    assertion.siUpper !== null
      ? `${number.format(assertion.siValue)}–${number.format(assertion.siUpper)}`
      : number.format(assertion.siValue);
  return withUnit(range, symbol);
}

/**
 * #929: a value's conditions, each as written -- "25 °C", "760 mmHg", "pH 7" -- and its solvent's
 * name, for a short line under a value. Units are written as the catalog writes them.
 */
export function conditionTexts(
  assertion: Pick<Assertion, 'conditions'>,
  catalog: readonly PropertyDefinition[]
): { readonly quantities: readonly string[]; readonly solvent: string | null } {
  const symbolOf = (code: string) =>
    catalog.map((property) => unitSymbol(code, property)).find((symbol) => symbol !== code) ?? unitSymbol(code);
  const quantities = assertion.conditions
    .filter((condition) => condition.kind !== 'solvent')
    .map((condition) =>
      condition.kind === 'ph' ? `pH ${condition.value}` : withUnit(condition.value, symbolOf(condition.unit))
    );
  const solvent = assertion.conditions.find((condition) => condition.kind === 'solvent');
  return { quantities, solvent: solvent ? (solvent.label ?? null) : null };
}

/** #931: temperature units a reader can switch between, as UCUM codes, from kelvin. */
const FROM_KELVIN: Record<string, (kelvin: number) => number> = {
  K: (kelvin) => kelvin,
  Cel: (kelvin) => kelvin - 273.15,
  '[degF]': (kelvin) => ((kelvin - 273.15) * 9) / 5 + 32
};

/** Whether a value in SI can be shown in this unit (today: temperatures, between K, °C and °F). */
export function canShowIn(siUnit: string, unit: string): boolean {
  return siUnit === 'K' && unit in FROM_KELVIN;
}

/** A number for display: up to six significant digits, no trailing noise ("135", "275", "-114.14"). */
function displayNumber(value: number, language: string): string {
  return new Intl.NumberFormat(language, { maximumSignificantDigits: 6, useGrouping: false }).format(
    Math.abs(value) < 1e-9 ? 0 : value
  );
}

/**
 * #931: a value converted to the reader's unit -- "135 °C" for one listed as "275 °F" -- or null when
 * it was already given in that unit, or can't be shown in it.
 */
export function formatIn(
  assertion: Pick<Assertion, 'value' | 'siValue' | 'siUpper' | 'siUnit'>,
  unit: string,
  property: PropertyDefinition | undefined,
  language: string
): string | null {
  if (assertion.value.unit === unit || !canShowIn(assertion.siUnit, unit)) return null;
  const convert = FROM_KELVIN[unit]!;
  const low = displayNumber(convert(assertion.siValue), language);
  const range = assertion.siUpper !== null ? `${low}–${displayNumber(convert(assertion.siUpper), language)}` : low;
  return withUnit(range, unitSymbol(unit, property));
}

/** #931: an SI difference written in the reader's unit -- a 1.1 K spread is "1.1 °C", or "2 °F" -- or SI. */
export function formatDifference(
  difference: number,
  siUnit: string,
  unit: string,
  property: PropertyDefinition | undefined,
  language: string
): string {
  const scale = siUnit === 'K' && unit === '[degF]' ? 9 / 5 : 1;
  const shownUnit = canShowIn(siUnit, unit) ? unit : siUnit;
  return withUnit(displayNumber(difference * scale, language), unitSymbol(shownUnit, property));
}
